
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { subscribeToMailchimp } from "@/utils/mailchimp";
import { SignUpFormFields } from "./components/SignUpFormFields";
import { useTempUserData } from "./hooks/useTempUserData";

interface SignUpFormProps {
  onModeChange: (mode: "signin") => void;
}

export const SignUpForm = ({ onModeChange }: SignUpFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const initialFormData = { email: "", password: "", name: "" };
  const { formData, setFormData } = useTempUserData(initialFormData);
  const [isTestMode, setIsTestMode] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedTier } = useSubscription();
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!isTestMode && !selectedTier) {
        toast({
          title: "Subscription Required",
          description: "Please select a subscription tier or enable test mode to continue.",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      const { error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      if (isTestMode) {
        // Create beta tester subscription
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: betaError } = await supabase.rpc('create_beta_tester_subscription', {
            user_id_param: user.id,
            tier_param: 'pro'
          });

          if (betaError) {
            console.error('Error creating beta subscription:', betaError);
          }
        }
      }

      // Add to Mailchimp with appropriate tag
      if (formData.name && formData.email) {
        const nameParts = formData.name.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined;
        
        try {
          await subscribeToMailchimp({
            email: formData.email,
            firstName,
            lastName,
            tags: ["signup", subscribeToNewsletter ? "newsletter" : "account-only"],
            status: "Subscribed"
          });
        } catch (mailchimpError) {
          console.error("Failed to add user to Mailchimp:", mailchimpError);
          // Don't stop the process for mailchimp errors
        }
      }

      toast({
        title: "Success!",
        description: "Please check your email to verify your account.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SignUpFormFields 
        formData={formData}
        setFormData={setFormData}
        isTestMode={isTestMode}
        setIsTestMode={setIsTestMode}
        subscribeToNewsletter={subscribeToNewsletter}
        setSubscribeToNewsletter={setSubscribeToNewsletter}
      />

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Loading..." : "Sign Up"}
      </Button>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => onModeChange("signin")}
          className="text-sm text-blue-600 hover:underline"
        >
          Already have an account? Sign in
        </button>
      </div>
    </form>
  );
};
